import { createContext, useContext, useState, useEffect } from 'react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('opsflow_token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          const res = await fetch(`${API_URL}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (res.ok) {
            const userData = await res.json();
            setUser(userData);
          } else {
            localStorage.removeItem('opsflow_token');
            setToken(null);
          }
        } catch (e) {
          console.error('Auth init error:', e);
          localStorage.removeItem('opsflow_token');
          setToken(null);
        }
      }
      setLoading(false);
    };
    initAuth();
  }, [token]);

  const login = async (email, password) => {
    try {
      const res = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      
      const text = await res.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        throw new Error('Server error. Please try again.');
      }
      
      if (!res.ok) {
        throw new Error(data.detail || 'Login failed');
      }
      
      localStorage.setItem('opsflow_token', data.token);
      setToken(data.token);
      setUser(data.user);
      return data;
    } catch (err) {
      console.error('Login error:', err);
      throw err;
    }
  };

  const register = async (userData) => {
    try {
      const res = await fetch(`${API_URL}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userData)
      });
      
      const text = await res.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        throw new Error('Server error. Please try again.');
      }
      
      if (!res.ok) {
        throw new Error(data.detail || 'Registration failed');
      }
      
      localStorage.setItem('opsflow_token', data.token);
      setToken(data.token);
      setUser(data.user);
      return data;
    } catch (err) {
      console.error('Register error:', err);
      throw err;
    }
  };

  const logout = () => {
    localStorage.removeItem('opsflow_token');
    setToken(null);
    setUser(null);
  };

  const authFetch = async (url, options = {}) => {
    // Always get fresh token from localStorage to avoid stale state
    const currentToken = localStorage.getItem('opsflow_token');
    
    if (!currentToken) {
      logout();
      throw new Error('No authentication token');
    }
    
    const headers = {
      ...options.headers,
      Authorization: `Bearer ${currentToken}`
    };
    
    if (options.body && typeof options.body === 'object' && !(options.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
      options.body = JSON.stringify(options.body);
    }
    
    const res = await fetch(`${API_URL}${url}`, { ...options, headers });
    
    if (res.status === 401) {
      logout();
      throw new Error('Session expired');
    }
    
    return res;
  };

  // Refresh user data from server
  const refreshUser = async () => {
    const currentToken = localStorage.getItem('opsflow_token');
    if (!currentToken) return;

    try {
      const res = await fetch(`${API_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${currentToken}` }
      });
      if (res.ok) {
        const userData = await res.json();
        setUser(userData);
      }
    } catch (e) {
      console.error('Refresh user error:', e);
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, authFetch, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};
